// src/services/storage/firebase.js
// Firebase initialization, Firestore helpers, and reconnect logic.
// Only active when VITE_ENABLE_FIREBASE=true.
// Import: import { initFirebase, withRetry, getDB } from '@services/storage/firebase'

import { FIREBASE, FEATURES } from '@config';

let _app  = null;
let _db   = null;
let _auth = null;

// ── Initialize ────────────────────────────────────────────────────
export async function initFirebase() {
  if (!FEATURES.firebase || !FIREBASE) return null;
  if (_app) return { app: _app, db: _db, auth: _auth };

  const { initializeApp, getApps }         = await import('firebase/app');
  const { getFirestore, initializeFirestore,
          persistentLocalCache,
          persistentMultipleTabManager,
          CACHE_SIZE_UNLIMITED }            = await import('firebase/firestore');
  const { getAuth,
          signInAnonymously,
          onAuthStateChanged }              = await import('firebase/auth');

  _app  = getApps().length ? getApps()[0] : initializeApp(FIREBASE);
  _auth = getAuth(_app);

  // ── Anonymous auth ────────────────────────────────────────────
  // Sign in anonymously so every user has a stable UID. This is
  // required for the trust/reputation pipeline, Cloud Function auth
  // context, and phone verification flows.
  //
  // signInAnonymously is idempotent — if the user already has a
  // cached anonymous credential it resolves immediately without a
  // network call. The UID is stable across sessions via IndexedDB.
  //
  // If sign-in fails (e.g. Auth not enabled in Firebase Console),
  // we log a warning but don't block the app — features that need
  // auth will fail gracefully on their own.
  const ensureAuth = new Promise((resolve) => {
    // Already signed in (cached credential)
    if (_auth.currentUser) { resolve(_auth.currentUser); return; }

    // Listen for auth state, then sign in
    const unsub = onAuthStateChanged(_auth, (user) => {
      unsub();
      if (user) {
        resolve(user);
      } else {
        signInAnonymously(_auth)
          .then(resolve)
          .catch((err) => {
            console.warn('[Firebase] Anonymous sign-in failed:', err.code,
              '— ensure Anonymous auth is enabled in Firebase Console → Authentication');
            resolve(null);
          });
      }
    });
  });

  // Wire analytics uid for beta event tracking
  if (_auth.currentUser) {
    import('@services/analytics').then(a => a.setAnalyticsUid(_auth.currentUser.uid)).catch(() => {});
  }

  await ensureAuth;

  // Use new FirestoreSettings.cache API (replaces deprecated enableIndexedDbPersistence)
  // persistentMultipleTabManager handles multiple tabs gracefully
  try {
    _db = initializeFirestore(_app, {
      cache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    });
  } catch (err) {
    // failed-precondition = multiple tabs open (expected) — fall back to memory cache
    // unimplemented = browser doesn't support IndexedDB (private browsing etc.)
    if (err.code !== 'failed-precondition' && err.code !== 'unimplemented') {
      console.warn('[Firebase] Persistent cache unavailable:', err.code ?? err.message);
    }
    try {
      _db = initializeFirestore(_app, { cacheSizeBytes: CACHE_SIZE_UNLIMITED });
    } catch {
      _db = getFirestore(_app);
    }
  }

  return { app: _app, db: _db, auth: _auth };
}

export const getDB   = () => _db;
export const getAuth = () => _auth;

// ── withRetry ─────────────────────────────────────────────────────
// Wraps any async Firestore operation with exponential backoff.
// Retries only on network-unavailable errors (not permission errors).
export async function withRetry(fn, { maxAttempts = 5, label = 'Firestore' } = {}) {
  let delay = 1000;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      const isLast    = attempt === maxAttempts;
      const isNetwork = err.code === 'unavailable' || err.message?.includes('network');
      if (import.meta.env.DEV) {
        console.warn(`[${label}] attempt ${attempt}/${maxAttempts}:`, err.message);
      }
      if (isLast || !isNetwork) throw err;
      await sleep(delay);
      delay = Math.min(delay * 2, 30_000);
    }
  }
}

// ── Reconnect monitor ─────────────────────────────────────────────
// Uses Firebase RTDB .info/connected for reliable connection detection.
// Applies exponential backoff: 5s → 15s → 45s → 2min → 5min cap.

let _connected      = true;
let _reconnectTimer = null;

export function initFirebaseReconnect(firestoreDb) {
  if (!FEATURES.firebase || !firestoreDb) return;

  import('firebase/database').then(({ getDatabase, ref, onValue }) => {
    try {
      const rtdb    = getDatabase();
      const connRef = ref(rtdb, '.info/connected');
      onValue(connRef, (snap) => {
        const now = snap.val() === true;
        if (now && !_connected) {
          _connected = true;
          clearTimeout(_reconnectTimer);
          window.dispatchEvent(new CustomEvent('na-firebase-reconnected'));
        }
        if (!now && _connected) {
          _connected = false;
          window.dispatchEvent(new CustomEvent('na-firebase-disconnected'));
          _scheduleReconnect(firestoreDb, 5_000);
        }
      });
    } catch (err) {
      console.warn('[Firebase] Reconnect monitor failed:', err);
    }
  }).catch(() => {
    // firebase/database not installed — skip
  });
}

function _scheduleReconnect(db, delayMs, attempt = 1) {
  clearTimeout(_reconnectTimer);
  _reconnectTimer = setTimeout(async () => {
    if (_connected) return;
    try {
      const { enableNetwork } = await import('firebase/firestore');
      await enableNetwork(db);
    } catch {
      _scheduleReconnect(db, Math.min(delayMs * 3, 5 * 60_000), attempt + 1);
    }
  }, delayMs);
}

// ── Connection change listeners ───────────────────────────────────
const _listeners = new Set();

export function onConnectionChange(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

if (typeof window !== 'undefined') {
  window.addEventListener('online',  () => _listeners.forEach((cb) => cb(true)));
  window.addEventListener('offline', () => _listeners.forEach((cb) => cb(false)));
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
